import type { Coverage } from "../api/sources";
import { KpiTile } from "../components/KpiTile";
import { Panel } from "../components/Panel";
import { quantity } from "../lib/format";
import styles from "./SourcesCoverage.module.css";

const METRICS = [
  { key: "sources", label: "Sources", note: "Expected data streams" },
  { key: "submitted", label: "Received bills", note: "Activity arrived" },
  { key: "missing", label: "Missing bills", note: "Expected, not received" },
  { key: "pending", label: "Pending bills", note: "Not closed yet" },
  { key: "unowned", label: "No owner", note: "Sources with nobody named" },
] as const;

/** The five facts at the head of Sources, as `KpiTile`s (the same object the home screen draws).
 *
 * **What a reader needs from them is the next place to go**, so the two that are somebody's to
 * chase carry a link: *missing* to the coverage grid, *no owner* to the register filtered to the
 * streams nobody holds. Only when there is something behind the figure - a link to an empty view
 * would be a control that goes nowhere.
 *
 * The tile's parts are found by the gate by their class (`_label_`, `_figure_`) and the tile by
 * `data-testid`; nothing here may use those words for something else. The pending tile carries the
 * unknown count in its note because *unknown* is the neighbour of *pending* and neither is missing.
 */
export function SourcesOverview({
  data,
  onShowGaps,
  onShowUnowned,
}: {
  data?: Coverage;
  onShowGaps?: () => void;
  onShowUnowned?: () => void;
}) {
  const known = Boolean(data && data.completeness.sources > 0);
  return (
    <section className={styles.overview} aria-label="Source coverage summary" data-testid="sources-summary" aria-busy={!data}>
      <div className={styles.metrics}>
        {METRICS.map(({ key, label, note }) => {
          const value = data?.completeness[key];
          const gap = known && (key === "missing" || key === "unowned") && (value ?? 0) > 0;
          const text = data ? (known || key === "sources" ? quantity(String(value), 0) : "—") : "";
          const unknown = data?.completeness.unknown ?? 0;
          const shown = key === "pending" && known && unknown > 0 ? `${note} · ${unknown} unknown` : note;
          const drill = gap
            ? key === "missing"
              ? onShowGaps && { label: "See the gaps", open: onShowGaps }
              : onShowUnowned && { label: "Show them", open: onShowUnowned }
            : undefined;
          return (
            <div
              key={key}
              className={`${styles.stat} ${gap ? styles.gap : ""} ${data ? "" : styles.pending}`}
              data-testid={key === "unowned" ? "stat-unowned" : `sources-stat-${key}`}
            >
              <KpiTile
                kpi={key}
                label={label}
                value={text}
                note={shown}
                footer={
                  drill ? (
                    <button type="button" className={styles.drill} onClick={drill.open}>
                      {drill.label}
                    </button>
                  ) : undefined
                }
              />
            </div>
          );
        })}
      </div>
      {!data ? <span role="status" className="visuallyHidden">Reading source coverage</span> : known ? (
        <p className={styles.stateSummary}>
          <strong>{quantity(String(data.completeness.declared_nil), 0)}</strong> periods confirmed as none
          <span aria-hidden="true"> · </span>
          <strong>{quantity(String(data.completeness.unknown), 0)}</strong> unknown on-event periods.
          Submitted counts windows overlapped by activity in force, not verified figures or documents.
        </p>
      ) : <p className={styles.stateSummary}>No sources declared. Coverage is unknown; zero missing bills would not establish completeness.</p>}
    </section>
  );
}

export function SourcesSiteCoverage({ data }: { data: Coverage }) {
  const sites = new Map<string, { id: string; name: string; missing: number }>();
  for (const source of data.sources) {
    const site = sites.get(source.facility_id) ?? { id: source.facility_id, name: source.facility_name, missing: 0 };
    site.missing += source.missing;
    sites.set(site.id, site);
  }
  const ordered = [...sites.values()].sort((left, right) => right.missing - left.missing || left.name.localeCompare(right.name) || left.id.localeCompare(right.id));
  const largest = Math.max(0, ...ordered.map((site) => site.missing));
  return (
    <div className={styles.sitePanel} data-testid="sources-site-coverage">
      <Panel title="Missing bills by site" sub="Every declared source, across the reporting window">
        {ordered.length ? <>
          {largest === 0 ? <p className={styles.note}>No bills are currently missing from the declared sources.</p> : null}
          <table className={styles.table}>
            <caption className="visuallyHidden">Missing expected bills by site</caption>
            <thead><tr><th scope="col">Site</th><th scope="col">Missing bills</th></tr></thead>
            <tbody>{ordered.map((site) => <tr key={site.id} data-site={site.id}>
              <th scope="row">{site.name}</th>
              <td>
                <span className={styles.amount}>{quantity(String(site.missing), 0)}</span>
                <span className={styles.track} aria-hidden="true"><span className={styles.fill} style={{ width: `${largest > 0 ? (site.missing / largest) * 100 : 0}%` }} /></span>
              </td>
            </tr>)}</tbody>
          </table>
          <p className={styles.note}>Counts expected-bill periods, not documents or emissions. Pending and unknown windows are not missing. Register filters do not change these counts.</p>
        </> : <p className={styles.note}>No sources have been declared, so missing bills cannot be assessed.</p>}
      </Panel>
    </div>
  );
}
